// The studio's routes (src/lib/studio/server.js), from the browser. Shown only when the site has a
// studio (NEXT_PUBLIC_STUDIO=1).
import { extensionOn } from "../../lib/extensions/registry";

export const studioOn = () => extensionOn("studio") || (process.env.NEXT_PUBLIC_STUDIO === "1" && typeof window !== "undefined" && new URLSearchParams(window.location.search).has("program"));

export class StudioRequestError extends Error {
  constructor(status, message) { super(message); this.status = status; }
}

// A track the studio made, as a mono 44.1 kHz WAV: { name, data (base64), type: "audio" }, as the DAW
// plugin's drag takes it (a WAV of up to 3 MB: 30 seconds fits).
export async function wavForDaw(url, name) {
  const [{ wavFile }, { toBase64 }] = await Promise.all([import("../../lib/audio-search/browser"), import("../../lib/plugin/bridge")]);
  const data = await (await fetch(url, { credentials: "same-origin" })).arrayBuffer(), context = new AudioContext({ sampleRate: 44100 });
  try {
    const buffer = await context.decodeAudioData(data), samples = new Float32Array(Math.min(buffer.length, 44100 * 32));
    for (let channel = 0; channel < buffer.numberOfChannels; channel++) { const values = buffer.getChannelData(channel); for (let i = 0; i < samples.length; i++) samples[i] += values[i] / buffer.numberOfChannels; }
    const file = wavFile(samples, buffer.sampleRate, `${name}.wav`);
    return { name, data: toBase64(new Uint8Array(await file.arrayBuffer())), type: "audio" };
  } finally { context.close().catch(() => {}); }
}

// A song sent straight to the studio (the site's own server takes at most 4.5 MB): "<job>/<file>", to
// give split and remake as `upload` in place of the song.
export async function uploadSong(file) {
  const { url, token } = await studioRequest("upload", { method: "POST", body: { size: file.size, type: file.type } });
  const form = new FormData();
  form.append("token", token);
  form.append("audio", file, file.name || "song");
  let response;
  try { response = await fetch(url, { method: "POST", body: form }); }
  catch { throw new StudioRequestError(503, "The song couldn't be sent to the studio. Check your connection and try again."); }
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new StudioRequestError(response.status, payload.detail || "The song couldn't be sent to the studio.");
  return `${payload.job}/${payload.file}`;
}

// JSON bodies as objects; a recording or song as a Blob (sent as is, with its type).
export async function studioRequest(path, { method = "GET", body, query } = {}) {
  const url = `/api/studio/${path}${query ? `?${new URLSearchParams(query)}` : ""}`;
  const raw = body instanceof Blob;
  const response = await fetch(url, { method, credentials: "same-origin", headers: body === undefined ? {} : { "Content-Type": raw ? body.type || "application/octet-stream" : "application/json" },
    body: body === undefined ? undefined : raw ? body : JSON.stringify(body) });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new StudioRequestError(response.status, payload.error || "The studio couldn't do that.");
  return payload;
}
